/**
 * collection-pin.js — lead a collection grid with the garments an ad showed.
 *
 *   /collections/all_pants?pin=<handle>
 *   /collections/all_pants?pin=<handle-a>,<handle-b>,<handle-c>
 *   /collections/all_pants?pin=<handle-a>,<handle-b>&variant=<id-a>,<id-b>
 *
 * Up to FIVE handles, comma separated, honoured left to right. Bed, 2 Nov 2026:
 * "i just need link that i will supply in colection page landing pages on
 * videos which has multiple proeucts" — a reel shows several looks, so the link
 * under it has to lead with all of them. "max 5".
 *
 * ⚠ BACKWARD COMPATIBLE BY CONSTRUCTION. A single handle has no comma, so every
 * URL already live in a Meta ad parses to a one-item list and behaves exactly as
 * before. Shopify handles are [a-z0-9-] only, so a comma can never appear inside
 * one and is a safe separator.
 *
 * `variant` is POSITIONAL and optional: `variant=111,,333` points the first and
 * third pinned cards at a size and leaves the second alone.
 *
 * ── WHY ANY OF THIS EXISTS ──────────────────────────────────────────────────
 *
 * Meta ads used to land on a product page, where 84.5% of sessions stopped —
 * no browse step for a visitor who did not want that exact garment. Ads now
 * land on the collection with the garments named in the URL.
 *
 * ── THREE THINGS THAT MAKE IT AWKWARD ───────────────────────────────────────
 *
 * 1. Liquid cannot read query parameters at all. There is no `params` object,
 *    so the pin can only be applied after the page arrives.
 *
 * 2. The pinned garments are usually NOT in the collection. All_* collections
 *    are far smaller than the catalogue (~1,930 pant products exist, All_Pants
 *    holds 121), so most pins are fetched and injected, not reordered.
 *
 * 3. Dawn's facets.js does `ProductGridContainer.innerHTML = …` on every
 *    filter, sort and back button, so cards injected once would vanish the
 *    first time anyone filtered. A MutationObserver re-applies them; Dawn emits
 *    no event to listen for instead.
 */
(function () {
  'use strict';

  var PINNED_CLASS = 'grid__item--pinned';
  var INJECTED_ATTR = 'data-pin-injected';
  var MAX_PINS = 5;

  /**
   * handle -> card element (fetched), or false (tried, nothing to show).
   * Undefined means "not fetched yet". Caching `false` is what stops a 404
   * being re-requested on every filter change.
   */
  var cards = {};
  var inFlight = {};

  /** Set while we mutate the grid, so our own writes do not re-trigger us. */
  var applying = false;

  function readParam(name) {
    try {
      var value = new URLSearchParams(window.location.search).get(name);
      return value ? value.trim() : '';
    } catch (e) {
      return '';
    }
  }

  /** Comma list -> de-duplicated handles, capped. Order is the ad's order. */
  function parseHandles(raw) {
    var out = [];
    var seen = {};
    var parts = raw.split(',');
    for (var i = 0; i < parts.length; i++) {
      var h = parts[i].trim();
      if (!h || seen[h]) continue;
      seen[h] = true;
      out.push(h);
      if (out.length === MAX_PINS) break;
    }
    return out;
  }

  /**
   * ⚠ READ ONCE, ON PURPOSE — facets.js REWRITES THE URL AND DROPS ?pin.
   *
   * FacetFiltersForm rebuilds the query string from the filter form alone, so
   * after the first sort or filter the URL is
   *   ?sort_by=most-relevant&filter.v.price.gte=&filter.v.price.lte=
   * and `pin` is simply gone. Re-reading the parameter on every pass would
   * therefore drop the pins the moment anyone touches a filter — which is the
   * case they most need to survive, because a visitor who filters is a visitor
   * who is still browsing. Verified in a browser, 23 Sep 2026.
   */
  var PINS = parseHandles(readParam('pin').toLowerCase());
  var VARIANTS = readParam('variant').split(',');

  function grid() {
    return document.getElementById('product-grid');
  }

  /**
   * The seats the section reserved, newest first.
   *
   * The grid renders five hidden <li data-pin-slot> cells; a blocking script
   * reveals exactly as many as the URL asked for. Consuming a seat in the same
   * synchronous pass as the insert is what stops the grid jumping a row ~200ms
   * after it settles.
   */
  function seats() {
    var g = grid();
    return g ? g.querySelectorAll('li[data-pin-slot]') : [];
  }

  /** Give up seats no card is coming for, so the grid carries no gap. */
  function releaseSeats(keep) {
    var s = seats();
    for (var i = 0; i < s.length - (keep || 0); i++) {
      if (s[i].parentNode) s[i].parentNode.removeChild(s[i]);
    }
    if (!seats().length) {
      document.documentElement.classList.remove('pin-pending');
      document.documentElement.removeAttribute('data-pin-count');
    }
  }

  /**
   * Put ?pin back after facets.js has dropped it, so a reload or a shared link
   * still leads with the advertised garments. replaceState rather than
   * pushState: repairing the current entry, not adding a back-button step.
   */
  function keepInUrl() {
    if (!PINS.length || !window.history || !window.history.replaceState) return;
    try {
      var want = PINS.join(',');
      var url = new URL(window.location.href);
      if (url.searchParams.get('pin') === want) return;
      url.searchParams.set('pin', want);
      var v = readParam('variant');
      if (v) url.searchParams.set('variant', v);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) {
      /* the pins still apply; only the URL is untidy */
    }
  }

  /** The product handle a grid cell points at, lowercased, or ''. */
  function handleOf(item) {
    var link = item.querySelector('a[href*="/products/"]');
    if (!link) return '';
    try {
      var path = new URL(link.href, window.location.origin).pathname;
      var at = path.indexOf('/products/');
      if (at === -1) return '';
      return path.slice(at + 10).split('?')[0].toLowerCase();
    } catch (e) {
      return '';
    }
  }

  /** Real product cells, in order — seats and CareOS module rows excluded. */
  function productItems() {
    var g = grid();
    if (!g) return [];
    return g.querySelectorAll(
      'li.grid__item:not(.grid__item--module):not([data-pin-slot])'
    );
  }

  /**
   * The <li> already in the grid for a handle, or null.
   *
   * Matched on the card's own link rather than a data attribute, because
   * data-product-handle lives on the quick-add control, which does not render
   * on every card. The link always does.
   */
  function findExisting(handle) {
    var items = productItems();
    for (var i = 0; i < items.length; i++) {
      if (items[i].hasAttribute(INJECTED_ATTR)) continue;
      if (handleOf(items[i]) === handle) return items[i];
    }
    return null;
  }

  /** The handles the grid currently leads with, as far as they are pinned. */
  function leadingHandles() {
    var items = productItems();
    var out = [];
    for (var i = 0; i < items.length; i++) {
      if (!items[i].classList.contains(PINNED_CLASS)) break;
      out.push(handleOf(items[i]));
    }
    return out;
  }

  /**
   * Point every link on a pinned card at the advertised size.
   *
   * The card has no size picker of its own, so preselecting a variant inside it
   * is not possible. Carrying ?variant through to the product page is — the
   * shopper lands on the size the ad showed them. That is the whole of what the
   * optional `variant` parameter can honestly buy here.
   */
  function carryVariant(root, variantId) {
    if (!variantId) return;
    var links = root.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      try {
        var url = new URL(links[i].href, window.location.origin);
        url.searchParams.set('variant', variantId);
        links[i].href = url.pathname + url.search;
      } catch (e) {
        /* leave the link alone rather than mangle it */
      }
    }
  }

  /**
   * Fetch the card fragment for a handle. Resolves to an element, or to false
   * when there is nothing safe to show.
   */
  function fetchCard(handle) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'products/' + encodeURIComponent(handle) + '?view=card';

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        // 404 for a handle that does not exist, 404 for one that is
        // unpublished. Both mean: render the collection as it was.
        return response.ok ? response.text() : null;
      })
      .then(function (html) {
        if (!html) return false;

        var parsed = new DOMParser().parseFromString(html, 'text/html');

        // ⚠ If the ?view= suffix were ever removed, Shopify would answer with
        // the FULL product page at status 200 and we would inject an entire
        // page into one grid cell. Requiring a card root and refusing anything
        // carrying page furniture is what stops that being silent.
        if (parsed.getElementById('MainContent') || parsed.querySelector('main, header, footer')) {
          return false;
        }

        var card = parsed.querySelector('.card-wrapper');
        return card || false;
      })
      .catch(function () {
        return false;
      });
  }

  /** Wrap a fetched card in a grid cell that matches the cells beside it. */
  function asGridItem(card) {
    var item = document.createElement('li');
    var sibling = productItems()[0];

    // Copy the neighbours' classes so the pinned cell inherits whatever the
    // section decided about animations and column spans, rather than guessing.
    item.className = sibling ? sibling.className : 'grid__item';
    item.classList.add(PINNED_CLASS);
    item.setAttribute(INJECTED_ATTR, '');

    // Animation order is per-cell and the pinned cards are now first.
    item.style.removeProperty('--animation-order');
    item.removeAttribute('data-cascade');

    item.appendChild(card);
    return item;
  }

  /** Drop cards we injected on a previous pass, so a rebuild cannot duplicate. */
  function clearInjected() {
    var g = grid();
    if (!g) return;
    var old = g.querySelectorAll('li[' + INJECTED_ATTR + ']');
    for (var i = 0; i < old.length; i++) {
      if (old[i].parentNode) old[i].parentNode.removeChild(old[i]);
    }
  }

  function startFetch(handle) {
    if (inFlight[handle]) return;
    inFlight[handle] = true;
    fetchCard(handle).then(function (card) {
      inFlight[handle] = false;
      cards[handle] = card || false;
      // The grid may have been replaced while we were fetching, so go back
      // through apply() rather than inserting blindly.
      apply();
    });
  }

  /**
   * Put the pinned products first, in the ad's order.
   *
   * Idempotent and safe to call repeatedly — it is called again after every
   * grid replacement. When the grid already leads with exactly what we can
   * show, it returns without touching the DOM, which is what stops this and
   * the MutationObserver chasing each other.
   */
  function apply() {
    if (!PINS.length) {
      releaseSeats(0);
      return;
    }

    var g = grid();
    if (!g) return;

    keepInUrl();

    // What can be shown right now, in order, and what is still to come.
    var show = [];
    var waiting = [];
    for (var i = 0; i < PINS.length; i++) {
      var handle = PINS[i];
      if (cards[handle] === false) continue;           // tried, nothing to show
      if (cards[handle] || findExisting(handle)) {
        show.push({ handle: handle, variant: (VARIANTS[i] || '').trim() });
      } else {
        waiting.push(handle);
      }
    }

    var lead = leadingHandles().join(',');
    var want = show.map(function (p) { return p.handle; }).join(',');

    if (lead !== want) {
      applying = true;
      clearInjected();

      var nodes = [];
      for (var j = 0; j < show.length; j++) {
        var node = findExisting(show[j].handle);
        if (node) {
          node.classList.add(PINNED_CLASS);
        } else {
          node = asGridItem(cards[show[j].handle].cloneNode(true));
        }
        carryVariant(node, show[j].variant);
        nodes.push(node);
      }

      // Consume a seat per card being placed, keeping one for each pin still
      // in flight. Same synchronous pass as the inserts, so the browser lays
      // out once and nothing visibly moves.
      releaseSeats(waiting.length);

      for (var k = nodes.length - 1; k >= 0; k--) g.insertBefore(nodes[k], g.firstChild);
      applying = false;
    }

    if (waiting.length) {
      for (var m = 0; m < waiting.length; m++) startFetch(waiting[m]);
    } else {
      releaseSeats(0);
    }
  }

  /**
   * Re-apply whenever facets.js swaps the grid out.
   *
   * Observing the container rather than the grid itself: facets.js replaces
   * #ProductGridContainer's innerHTML, so the #product-grid node we were
   * watching is discarded along with everything in it.
   */
  function watch() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || typeof MutationObserver === 'undefined') return;

    new MutationObserver(function () {
      if (applying) return;
      apply();
    }).observe(container, { childList: true, subtree: true });
  }

  function start() {
    apply();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
